// src/utils/liveRotation.ts
//
// Automatická rotace rund po stanovištích živého bodování (krok 4,
// docs/live-auto-rotace-navrh.md a docs/live-auto-rotace-rozbor.md §4).
//
// Jeden výpočet pro nastavení (náhled, race.rotation při spuštění) a obrazovku
// rozhodčího. Rotace se při spuštění ZMRAZÍ: race.rotationStations = id
// stanovišť podle čísla a race.rotation = startovní POZICE každé rundy
// (1..N v rotationStations). k-tá runda (podle čísla, k = 1..R) začíná na
// pozici ((S − 1) + (k − 1)) mod N + 1 – rundy navíc (víc rund než stanovišť)
// i čísla rund s mezerou se rozmístí podle pořadí, runda navíc jde po rundě
// se stejným startem.
//
// Kde je která runda, se ODVOZUJE z dat, která obrazovka už zobrazuje
// (výsledky, rundy, stanoviště, uvolnění live_progress/{stationId}_r{N}) –
// žádný uložený „průběh“, žádný zápis pro rotaci. Bez signálu tak nic nejde
// přepsat a vlastní výsledky (i ve frontě) posouvají rundy hned:
//
//   route(r)      = rotationStations otočené na start rundy r
//   has(s, X)     = výsledek střelce s na stanovišti X (!= null)
//   withdrawn(r,i)= střelci ze seznamu „chybí“ uvolnění rundy na route[j],
//                   j < i, kteří od route[j] (včetně) nemají žádný výsledek
//   expected(r,i) = aktivní střelci rundy − withdrawn(r, i)
//   done(r, i)    = stanoviště už neexistuje || uvolnění || expected mají výsledek
//   hw(r)         = nejvyšší i, kde má runda nějaký výsledek (jinak 0)
//   pos(r)        = nejnižší i ≥ hw(r), kde runda není hotová; žádné = hotovo
//
// Neúplná data z jiného stanoviště můžou rundu jen „zdržet“ (předchozí
// stanoviště vypadá nehotové), nikdy ukončit předčasně: koho runda čeká,
// mění jen výslovné uvolnění „chybí #N“, ne to, co telefon zrovna vidí.
//
// Pořadí rund na stanovišti (fronta): runda, která tu už střílí (výsledek,
// nebo potvrzená na tomto telefonu), pak podle pořadí stanoviště na trase
// rundy (kdo měl stanoviště dřív na trase, přišel dřív – bez hodin, rozbor
// §4.4), pak podle pořadí rundy (sdílený start: nižší runda první).

/** Jedna runda pro výpočet (jen to, co rotace potřebuje). */
export interface RotationRoundInput {
  number: number;
  shooters: ReadonlyArray<{ startNumber: number; active?: boolean }>;
}

/** Uvolnění rundy na stanovišti (live_progress/{stationId}_r{N}). */
export interface RotationMarker {
  stationId: string;
  roundNumber: number;
  released: boolean;
  /** Startovní čísla, která v rundě chyběla (u terče prázdné). */
  missing: number[];
}

/** Rotace zmrazená při spuštění (race.rotationStations, race.rotation). */
export interface FrozenRotation {
  /** Id stanovišť v pořadí čísel při spuštění. */
  stations: string[];
  /** Číslo rundy (text) → startovní pozice 1..N. */
  starts: Record<string, number>;
}

type ResultsLike = { [startNumber: number]: { stations?: { [stationId: string]: unknown } | null } | undefined | null };

/** Výsledek střelce na stanovišti existuje (i nula; null/undefined ne) – jeden pomocník pro všechny. */
export function hasStationResult(results: ResultsLike, startNumber: number, stationId: string): boolean {
  const r = results[startNumber];
  const stations = r ? r.stations : undefined;
  return !!stations && typeof stations === "object" && stations[stationId] != null;
}

/** Startovní stanoviště mimo 1..N (smazaná stanoviště) → 1; jedno pravidlo pro náhled, výběr i spuštění. */
export function normalizeStartStation(start: unknown, totalStations: number): number {
  const n = Math.floor(totalStations);
  return typeof start === "number" && Number.isInteger(start) && start >= 1 && start <= n ? start : 1;
}

/** Je startovní stanoviště mimo rozsah (upozornění v nastavení)? */
export const startStationOutOfRange = (start: unknown, totalStations: number): boolean =>
  typeof start === "number" && Number.isFinite(start) && totalStations >= 1 && normalizeStartStation(start, totalStations) !== start;

/** Čísla rund seřazená a bez opakování (pořadí rund pro rozmístění). */
function sortedRounds(roundNumbers: ReadonlyArray<number>): number[] {
  return Array.from(new Set(roundNumbers.filter((r) => Number.isFinite(r)))).sort((a, b) => a - b);
}

export interface RotationPlanRow {
  /** Číslo rundy. */
  round: number;
  /** Startovní pozice = číslo stanoviště při souvislých číslech 1..N. */
  station: number;
  /** Runda se stejným startem, po které tato runda jde (nejbližší nižší), jinak null. */
  after: number | null;
}

/**
 * Náhled rotace: startovní pozice každé rundy podle POŘADÍ rundy (ne čísla) a
 * s kým ji sdílí. Start mimo rozsah se normalizuje (1).
 */
export function rotationPlan(roundNumbers: ReadonlyArray<number>, totalStations: number, startStationForRound1: number): RotationPlanRow[] {
  const n = Math.floor(totalStations);
  if (!(n >= 1)) return [];
  const start = normalizeStartStation(startStationForRound1, n);
  const lastOnStation = new Map<number, number>();
  return sortedRounds(roundNumbers).map((round, k) => {
    const station = ((start - 1 + k) % n) + 1;
    const after = lastOnStation.has(station) ? (lastOnStation.get(station) as number) : null;
    lastOnStation.set(station, round);
    return { round, station, after };
  });
}

/** Mapa runda → startovní pozice (race.rotation při spuštění). */
export function rotationMap(roundNumbers: ReadonlyArray<number>, totalStations: number, startStationForRound1: number): Record<string, number> {
  const map: Record<string, number> = {};
  rotationPlan(roundNumbers, totalStations, startStationForRound1).forEach((row) => {
    map[String(row.round)] = row.station;
  });
  return map;
}

/**
 * Proč automatickou rotaci nejde spustit (prázdné pole = jde): čísla
 * stanovišť musí jít po sobě (pořadí se zmrazí podle čísel). Víc rund než
 * stanovišť a mezery v číslech rund nevadí (rozmístění podle pořadí, otázka 7).
 */
export function autoRotationProblems(stationNumbers: ReadonlyArray<number>): string[] {
  const stations = [...stationNumbers].sort((a, b) => a - b);
  if (stations.length === 0) return ["Závod nemá žádné stanoviště."];
  if (!stations.every((n, i) => n === i + 1)) {
    return ["Čísla stanovišť nejdou po sobě (1, 2, 3, …) – automatická rotace by rundy poslala na neexistující stanoviště."];
  }
  return [];
}

/**
 * Zmrazená rotace závodu, nebo null = závod není automatický (ruční rundy):
 * autoRotation === true a platné rotationStations (spuštění novou verzí).
 * Závod spuštěný starší verzí (bez rotationStations) je ruční.
 */
export function frozenRotationOf(race: Record<string, unknown> | null | undefined): FrozenRotation | null {
  if (!race || race.autoRotation !== true || !Array.isArray(race.rotationStations)) return null;
  const stations = (race.rotationStations as unknown[]).filter((s): s is string => typeof s === "string" && s !== "");
  if (stations.length === 0 || new Set(stations).size !== stations.length) return null;
  const starts: Record<string, number> = {};
  const raw = race.rotation && typeof race.rotation === "object" ? (race.rotation as Record<string, unknown>) : {};
  Object.keys(raw).forEach((k) => {
    const v = raw[k];
    if (typeof v === "number" && Number.isInteger(v) && v >= 1 && v <= stations.length) starts[k] = v;
  });
  return { stations, starts };
}

/** Uvolnění z dokumentů live_progress (starý průběh r{N} bez stationId se ignoruje). */
export function markersOf(docs: ReadonlyArray<{ id: string; data: Record<string, unknown> }>): RotationMarker[] {
  const out: RotationMarker[] = [];
  docs.forEach(({ data }) => {
    if (typeof data.stationId !== "string" || !data.stationId) return;
    const round = typeof data.roundNumber === "number" ? data.roundNumber : Number(data.roundNumber);
    if (!Number.isFinite(round)) return;
    const missing = Array.isArray(data.missing) ? (data.missing as unknown[]).filter((n): n is number => typeof n === "number") : [];
    out.push({ stationId: data.stationId, roundNumber: round, released: data.released === true, missing });
  });
  return out;
}

/** Id dokumentu uvolnění rundy na stanovišti. */
export const markerDocId = (stationId: string, roundNumber: number): string => `${stationId}_r${roundNumber}`;

/** Stav jedné rundy na jednom stanovišti její trasy. */
export interface RoundStop {
  stationId: string;
  /** Stanoviště pořád existuje (jinak se přeskočí). */
  live: boolean;
  /** Uvolněno (chybí #N / terč odstřílel). */
  released: boolean;
  /** Seznam „chybí“ z uvolnění. */
  releasedMissing: number[];
  /** Očekávaní střelci (aktivní bez těch, kdo chyběli dřív). */
  expected: number[];
  /** Mimo pořadí: chyběli na dřívějším stanovišti a od té doby nestříleli. */
  withdrawn: number[];
  /** Očekávaní s výsledkem. */
  doneCount: number;
  done: boolean;
  /** Runda tu má aspoň jeden výsledek. */
  touched: boolean;
}

export interface RoundRotation {
  round: number;
  /** Pořadí rundy (0..R−1) – rozhodne při sdíleném startu. */
  index: number;
  /** Startovní pozice 1..N; null = runda mimo rotaci (přidaná starší verzí). */
  start: number | null;
  /** Trasa (id stanovišť); prázdná mimo rotaci. */
  route: string[];
  stops: RoundStop[];
  /** Index aktuálního stanoviště na trase; null = hotovo nebo mimo rotaci. */
  pos: number | null;
  finished: boolean;
  /** Runda bez aktivních střelců (ignoruje se). */
  empty: boolean;
}

export interface RotationState {
  frozen: FrozenRotation;
  rounds: RoundRotation[];
  /** Živá stanoviště, která nejsou v rotaci (přidaná za běhu starší verzí). */
  stationsOutside: string[];
}

export interface RotationInput {
  frozen: FrozenRotation;
  /** Živá stanoviště (id). */
  stationIds: ReadonlyArray<string>;
  rounds: ReadonlyArray<RotationRoundInput>;
  results: ResultsLike;
  markers: ReadonlyArray<RotationMarker>;
}

/** Odvození polohy všech rund (bez zápisu; stejná data → stejný výsledek na každém telefonu). */
export function deriveRotation(input: RotationInput): RotationState {
  const { frozen, results } = input;
  const live = new Set(input.stationIds);
  const released = new Map<string, RotationMarker>();
  input.markers.forEach((m) => {
    if (m.released) released.set(markerDocId(m.stationId, m.roundNumber), m);
  });
  const n = frozen.stations.length;
  const ordered = [...input.rounds].filter((r) => Number.isFinite(r.number)).sort((a, b) => a.number - b.number);
  const rounds: RoundRotation[] = ordered.map((r, index) => {
    const startRaw = frozen.starts[String(r.number)];
    const start = typeof startRaw === "number" ? startRaw : null;
    const active = Array.from(new Set(r.shooters.filter((s) => s.active === true).map((s) => s.startNumber)));
    const all = Array.from(new Set(r.shooters.map((s) => s.startNumber)));
    const empty = active.length === 0;
    if (start === null || n === 0) {
      return { round: r.number, index, start: null, route: [], stops: [], pos: null, finished: false, empty };
    }
    const route = [...frozen.stations.slice(start - 1), ...frozen.stations.slice(0, start - 1)];
    // Chybějící podle uvolnění: startovní číslo → stále mimo pořadí
    const out = new Set<number>();
    const stops: RoundStop[] = route.map((stationId) => {
      const withdrawn = active.filter((sn) => out.has(sn));
      const expected = active.filter((sn) => !out.has(sn));
      const marker = released.get(markerDocId(stationId, r.number)) || null;
      const doneCount = expected.filter((sn) => hasStationResult(results, sn, stationId)).length;
      const isLive = live.has(stationId);
      const stop: RoundStop = {
        stationId,
        live: isLive,
        released: !!marker,
        releasedMissing: marker ? marker.missing.slice() : [],
        expected,
        withdrawn,
        doneCount,
        done: !isLive || !!marker || doneCount === expected.length,
        touched: all.some((sn) => hasStationResult(results, sn, stationId)),
      };
      // Výsledek tady = střelec je zpátky (od tohoto stanoviště čekaný dál)
      active.forEach((sn) => {
        if (out.has(sn) && hasStationResult(results, sn, stationId)) out.delete(sn);
      });
      // Uvolnění „chybí“ platí pro další stanoviště (kdo tu výsledek dostal, nechybí)
      if (marker) {
        marker.missing.forEach((sn) => {
          if (active.includes(sn) && !hasStationResult(results, sn, stationId)) out.add(sn);
        });
      }
      return stop;
    });
    let hw = 0;
    stops.forEach((s, i) => {
      if (s.touched) hw = i;
    });
    let pos: number | null = null;
    if (!empty) {
      for (let i = hw; i < stops.length; i++) {
        if (!stops[i].done) {
          pos = i;
          break;
        }
      }
    }
    return { round: r.number, index, start, route, stops, pos, finished: !empty && pos === null, empty };
  });
  const inRotation = new Set(frozen.stations);
  return { frozen, rounds, stationsOutside: input.stationIds.filter((id) => !inRotation.has(id)) };
}

/** Runda podle čísla. */
export const roundRotationOf = (state: RotationState, round: number): RoundRotation | null =>
  state.rounds.find((r) => r.round === round) || null;

/** Zastávka rundy na stanovišti (null = stanoviště není na trase rundy). */
export function stopOf(state: RotationState, round: number, stationId: string): RoundStop | null {
  const r = roundRotationOf(state, round);
  if (!r) return null;
  const i = r.route.indexOf(stationId);
  return i >= 0 ? r.stops[i] : null;
}

/**
 * Fronta stanoviště: rundy, které podle dat stojí na stanovišti (neprázdné,
 * nehotové), v pořadí obsluhy. `engagedRound` = runda potvrzená (nebo
 * rozpracovaná) na tomto telefonu – má přednost jako runda s výsledkem tady.
 */
export function stationQueue(state: RotationState, stationId: string, engagedRound: number | null = null): RoundRotation[] {
  const here = state.rounds.filter((r) => !r.empty && r.pos !== null && r.route[r.pos] === stationId);
  const engaged = (r: RoundRotation) => (r.round === engagedRound || r.stops[r.pos as number].touched ? 0 : 1);
  return here.sort((a, b) => engaged(a) - engaged(b) || (a.pos as number) - (b.pos as number) || a.index - b.index);
}

/**
 * Navržená runda pro telefon bez potvrzené rundy: první ve frontě, ale
 * předchozí návrh zůstane, dokud je pořád ve frontě (hystereze – změna
 * pořadí jinde návrh pod rukou rozhodčího nepřehodí).
 */
export function suggestedRound(state: RotationState, stationId: string, previous: number | null): number | null {
  const queue = stationQueue(state, stationId);
  if (previous !== null && queue.some((r) => r.round === previous)) return previous;
  return queue.length > 0 ? queue[0].round : null;
}

/** Runda, která na stanovišti přijde (čekací obrazovka). */
export interface UpcomingRound {
  round: number;
  /** Kolik stanovišť jí zbývá sem (1 = je na předchozím). */
  hops: number;
  /** Kde teď je. */
  atStationId: string;
  /** Je tam první na řadě (střílí / přijde hned); jinak čeká na uvolnění stanoviště. */
  firstThere: boolean;
  /** Očekávaní s výsledkem / očekávaní na stanovišti, kde teď je. */
  doneCount: number;
  expectedCount: number;
}

/** Rundy, které na stanoviště teprve přijdou: podle počtu stanovišť cesty, pak kdo je na svém stanovišti na řadě. */
export function upcomingRounds(state: RotationState, stationId: string): UpcomingRound[] {
  const out: Array<UpcomingRound & { index: number }> = [];
  state.rounds.forEach((r) => {
    if (r.empty || r.pos === null) return;
    const idx = r.route.indexOf(stationId);
    if (idx < 0 || idx <= r.pos || r.stops[idx].done) return;
    const at = r.route[r.pos];
    const first = stationQueue(state, at)[0];
    const stop = r.stops[r.pos];
    out.push({
      round: r.round,
      index: r.index,
      hops: idx - r.pos,
      atStationId: at,
      firstThere: !!first && first.round === r.round,
      doneCount: stop.doneCount,
      expectedCount: stop.expected.length,
    });
  });
  // Stejný počet stanovišť = stejné stanoviště, kde teď jsou: dřív přijde ta, která je tam na řadě
  return out
    .sort((a, b) => a.hops - b.hops || Number(b.firstThere) - Number(a.firstThere) || a.index - b.index)
    .map(({ index: _index, ...rest }) => rest);
}

/**
 * Chybějící výsledky rundy na stanovištích, která už prošla (přeskočená
 * stanoviště, smazaný výsledek) – přehled hlavního rozhodčího.
 */
export function skippedResults(r: RoundRotation, results: ResultsLike): Array<{ stationId: string; startNumbers: number[] }> {
  const upto = r.pos === null ? r.stops.length : r.pos;
  const out: Array<{ stationId: string; startNumbers: number[] }> = [];
  for (let i = 0; i < upto; i++) {
    const s = r.stops[i];
    if (!s.live) continue;
    const missing = s.expected.filter((sn) => !hasStationResult(results, sn, s.stationId));
    const notReleased = s.released ? missing.filter((sn) => !s.releasedMissing.includes(sn)) : missing;
    if (notReleased.length > 0) out.push({ stationId: s.stationId, startNumbers: notReleased });
  }
  return out;
}
